import * as React from 'react';
import { cn } from '@/lib/utils';

type TableContextValue = {
  stickyHeader: boolean;
  compact: boolean;
};

const TableContext = React.createContext<TableContextValue>({
  stickyHeader: false,
  compact: false
});

type TableProps = React.TableHTMLAttributes<HTMLTableElement> & {
  containerClassName?: string;
  stickyHeader?: boolean;
  compact?: boolean;
};

export function Table({ className, containerClassName, stickyHeader = false, compact = false, ...props }: TableProps) {
  return (
    <TableContext.Provider value={{ stickyHeader, compact }}>
      <div
        data-compact={compact ? '' : undefined}
        data-sticky-header={stickyHeader ? '' : undefined}
        className={cn(
          'brand-table-shell relative w-full overflow-auto rounded-xl border border-border/70 shadow-[inset_0_1px_0_rgba(255,255,255,0.5)]',
          'before:pointer-events-none before:absolute before:bottom-0 before:left-0 before:top-0 before:w-5 before:bg-gradient-to-r before:from-card/80 before:to-transparent before:content-[""]',
          'after:pointer-events-none after:absolute after:bottom-0 after:right-0 after:top-0 after:w-5 after:bg-gradient-to-l after:from-card/80 after:to-transparent after:content-[""]',
          containerClassName
        )}
      >
        <table className={cn('w-full min-w-full caption-bottom text-sm [font-variant-numeric:tabular-nums]', compact && 'text-[13px]', className)} {...props} />
      </div>
    </TableContext.Provider>
  );
}

export function TableHeader({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <thead className={cn('border-b border-border/80', className)} {...props} />;
}

export function TableBody({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={cn('[&_tr:last-child]:border-0', className)} {...props} />;
}

export function TableFooter({ className, ...props }: React.HTMLAttributes<HTMLTableSectionElement>) {
  return <tfoot className={cn('border-t border-border bg-secondary/60 font-medium', className)} {...props} />;
}

export function TableRow({ className, ...props }: React.HTMLAttributes<HTMLTableRowElement>) {
  return <tr className={cn('border-b border-border/65 transition-colors odd:bg-background/35 hover:bg-secondary/40', className)} {...props} />;
}

type TableHeadProps = React.ThHTMLAttributes<HTMLTableCellElement> & {
  numeric?: boolean;
};

export function TableHead({ className, numeric = false, ...props }: TableHeadProps) {
  const context = React.useContext(TableContext);

  return (
    <th
      className={cn(
        'h-11 bg-secondary/88 px-4 text-left align-middle text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground backdrop-blur',
        context.stickyHeader && 'sticky top-0 z-10 shadow-[inset_0_-1px_0_rgba(215,201,183,0.95)]',
        context.compact && 'h-10 px-3',
        numeric && 'text-right',
        className
      )}
      {...props}
    />
  );
}

type TableCellProps = React.TdHTMLAttributes<HTMLTableCellElement> & {
  numeric?: boolean;
};

export function TableCell({ className, numeric = false, ...props }: TableCellProps) {
  const context = React.useContext(TableContext);

  return <td className={cn('p-4 align-middle text-sm', context.compact && 'p-3', numeric && 'text-right tabular-nums', className)} {...props} />;
}

export function TableCaption({ className, ...props }: React.HTMLAttributes<HTMLTableCaptionElement>) {
  return <caption className={cn('mt-4 text-sm text-muted-foreground', className)} {...props} />;
}
